import type { ApiErrorCode, ApiFieldError, ErrorResponse } from '@/types/api';

/**
 * A failed API call, normalised.
 *
 * Every failure the app can encounter — a validation rejection, a network
 * outage, an HTML error page from a misconfigured proxy — arrives at the UI as
 * one of these. Without that, each call site would need its own guesswork about
 * what shape `error` happens to have.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly fieldErrors: ApiFieldError[];
  readonly requestId?: string;

  constructor(params: {
    code: ApiErrorCode;
    message: string;
    status: number;
    fieldErrors?: ApiFieldError[];
    requestId?: string;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.status = params.status;
    this.fieldErrors = params.fieldErrors ?? [];
    this.requestId = params.requestId;
  }

  /** True when the server rejected specific inputs, so a form can attach messages. */
  get isValidation(): boolean {
    return this.code === 'VALIDATION_FAILED' && this.fieldErrors.length > 0;
  }

  get isAuthentication(): boolean {
    return this.status === 401;
  }

  get isPermission(): boolean {
    return this.code === 'ACCESS_DENIED';
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Builds from a server error envelope. */
  static fromResponse(status: number, body: ErrorResponse): ApiError {
    return new ApiError({
      code: body.code,
      message: body.message,
      status,
      fieldErrors: body.errors,
      requestId: body.requestId,
    });
  }

  /**
   * Builds for a failure that never reached the API, or came back in a shape the
   * API never produces — a dropped connection, a proxy error page, a 502 from a
   * load balancer. The message is deliberately about connectivity rather than
   * echoing whatever the intermediary said.
   */
  static fromTransport(status: number, message?: string): ApiError {
    return new ApiError({
      code: status === 0 ? 'INTERNAL_ERROR' : 'INTERNAL_ERROR',
      message: message ?? 'Could not reach the server. Check your connection and try again.',
      status,
    });
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
